import React, { useEffect, useState } from "react";
import {
  Dialog,
  DialogContent,
  Box,
  Typography,
  IconButton,
  Tabs,
  Tab,
  alpha,
  useTheme,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import ScheduleIcon from "@mui/icons-material/Schedule";
import PeopleIcon from "@mui/icons-material/People";
import MedicationIcon from "@mui/icons-material/Medication";
import EditIcon from "@mui/icons-material/Edit";
import EditSchedule from "./EditSchedule";
import EditEmployee from "./EditEmployee";
import VisitMedication from "./VisitMedication";

const TABS = [
  { value: "schedule", label: "Schedule", icon: ScheduleIcon },
  { value: "assignment", label: "Assignment", icon: PeopleIcon },
  { value: "medication", label: "Medication", icon: MedicationIcon },
];

const EditScheduleModal = ({ show, handleClose, visitId, clientId, area }) => {
  const theme = useTheme();
  const [activeKey, setActiveKey] = useState("schedule");

  useEffect(() => {
    if (show) {
      setActiveKey("schedule");
    }
  }, [show]);

  const handleTabChange = (event, newValue) => {
    setActiveKey(newValue);
  };

  const handleScheduleSuccess = () => {
    setActiveKey("assignment");
  };

  return (
    <Dialog
      open={show}
      onClose={handleClose}
      maxWidth="lg"
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: "20px",
          boxShadow:
            "0px 32px 64px -16px rgba(16, 24, 40, 0.28), 0px 0px 0px 1px rgba(16, 24, 40, 0.04)",
          maxHeight: "92vh",
          height: "92vh",
          overflow: "hidden",
          width: "100%",
          bgcolor: "#FFFFFF",
        },
      }}
    >
      <DialogContent
        sx={{
          p: 0,
          overflow: "hidden",
          display: "flex",
          flexDirection: "column",
          bgcolor: "#FAFBFC",
        }}
      >
        {/* Header */}
        <Box
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            px: 3,
            py: 2.25,
            bgcolor: "#FFFFFF",
            borderBottom: "1px solid #F2F4F7",
            flexShrink: 0,
          }}
        >
          <Box sx={{ display: "flex", alignItems: "center", gap: 1.75 }}>
            <Box
              sx={{
                width: 42,
                height: 42,
                borderRadius: "12px",
                background:
                  "linear-gradient(135deg, #2469F0 0%, #4E89FF 100%)",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                boxShadow: "0 6px 16px rgba(36,105,240,0.28)",
              }}
            >
              <EditIcon sx={{ color: "#FFFFFF", fontSize: 22 }} />
            </Box>
            <Box>
              <Typography
                sx={{
                  fontSize: 17,
                  fontWeight: 700,
                  color: "#101828",
                  letterSpacing: "-0.01em",
                  lineHeight: 1.2,
                }}
              >
                Edit Visit
              </Typography>
              <Typography
                sx={{
                  fontSize: 12,
                  color: "#667085",
                  fontWeight: 500,
                  mt: 0.25,
                }}
              >
                Update schedule, assignment & medication
              </Typography>
            </Box>
          </Box>

          <IconButton
            onClick={handleClose}
            size="small"
            sx={{
              color: "#667085",
              border: "1px solid #EAECF0",
              borderRadius: "10px",
              width: 36,
              height: 36,
              "&:hover": {
                bgcolor: "#F9FAFB",
                borderColor: "#D0D5DD",
                color: "#101828",
              },
            }}
          >
            <CloseIcon sx={{ fontSize: 18 }} />
          </IconButton>
        </Box>

        {/* Tabs */}
        <Box
          sx={{
            bgcolor: "#FFFFFF",
            px: 3,
            borderBottom: "1px solid #F2F4F7",
            flexShrink: 0,
          }}
        >
          <Tabs
            value={activeKey}
            onChange={handleTabChange}
            variant="standard"
            sx={{
              minHeight: 48,
              "& .MuiTabs-flexContainer": {
                gap: 3,
              },
              "& .MuiTab-root": {
                textTransform: "none",
                fontWeight: 600,
                fontSize: 13.5,
                py: 1.5,
                px: 0,
                minHeight: 48,
                minWidth: 0,
                color: "#667085",
                transition: "color 0.18s ease",
                "&:hover": {
                  color: "#101828",
                },
                "&.Mui-selected": {
                  color: "#2469F0",
                },
              },
              "& .MuiTabs-indicator": {
                height: 2,
                borderRadius: "2px 2px 0 0",
                bgcolor: "#2469F0",
              },
            }}
          >
            {TABS.map(({ value, label, icon: Icon }) => (
              <Tab
                key={value}
                value={value}
                icon={
                  <Icon
                    sx={{
                      fontSize: 17,
                      mb: 0,
                      mr: 0.75,
                    }}
                  />
                }
                iconPosition="start"
                label={label}
                disableRipple
              />
            ))}
          </Tabs>
        </Box>

        {/* Content */}
        <Box
          sx={{
            p: 3,
            flex: 1,
            overflowY: "auto",
            overflowX: "hidden",
            bgcolor: "#FAFBFC",
            scrollbarWidth: "thin",
            "&::-webkit-scrollbar": {
              width: 8,
            },
            "&::-webkit-scrollbar-track": {
              bgcolor: "transparent",
            },
            "&::-webkit-scrollbar-thumb": {
              bgcolor: "#E4E7EC",
              borderRadius: 10,
              border: "2px solid #FAFBFC",
              "&:hover": {
                bgcolor: "#D0D5DD",
              },
            },
          }}
        >
          {activeKey === "schedule" && (
            <EditSchedule
              visitId={visitId}
              clientId={clientId}
              onHide={handleClose}
              onSuccess={handleScheduleSuccess}
            />
          )}
          {activeKey === "assignment" && (
            <EditEmployee
              key={`assignment-${visitId}`}
              visitId={visitId}
              clientId={clientId}
              activeKey={activeKey}
              area={area}
            />
          )}
          {activeKey === "medication" && (
            <VisitMedication visitId={visitId} clientId={clientId} />
          )}
        </Box>
      </DialogContent>
    </Dialog>
  );
};

export default EditScheduleModal;